/**
 * Setup: who can approve what, and the two states that actually block work.
 *
 * The coverage panel distinguishes **no Sponsor at all** from **Sponsor known but has no account
 * yet**, because they need different fixes: the first is an EMS or assignment problem, the second
 * is a Clerk invitation with a lead time. At go-live the second is the common one, and treating
 * them as one problem is how a fortnight gets lost.
 *
 * The Director panel leads, because it is the one thing here whose absence stops *everything*:
 * without a designated HR Director — and an admin who is not one — no claim can complete (D-39).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/session';
import { ErrorNote, PageHeader } from '@/components/bits';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Field,
  Input,
  Select,
  Table,
  Td,
  Th,
} from '@/components/ui';
import { ROLE_LABEL } from '@/lib/policy';
import { formatDate, formatInstant } from '@/lib/dates';
import type { CoverageRow } from '@/lib/types';

export function HrSetup() {
  const coverage = useQuery({ queryKey: ['sponsor-coverage'], queryFn: api.sponsorCoverage });
  const data = coverage.data;

  return (
    <>
      <PageHeader
        title="Setup"
        subtitle="Who can approve what, and who is still missing an account."
      />

      <div className="grid gap-6">
        <Directors />

        {data && data.no_sponsor.length > 0 ? (
          <Alert tone="bad" title={`${data.no_sponsor.length} active departments have no Sponsor`}>
            Their claims cannot route — there is nobody to endorse them. Sponsors arrive from EMS as
            the department head and deputy (D-41), so a gap here usually means the EMS record is
            missing a head.
            <ul className="mt-2 grid gap-0.5">
              {data.no_sponsor.map((row) => (
                <li key={row.department_id}>{row.department_name}</li>
              ))}
            </ul>
          </Alert>
        ) : null}

        {data && data.sponsor_without_account.length > 0 ? (
          <Alert
            tone="warn"
            title={`${uninvited(data.sponsor_without_account).length} people to invite, covering ${data.sponsor_without_account.length} departments`}
          >
            A different problem with a different fix: the app-side grant is already waiting — the
            sync allowlists them (D-44) — so all that is missing is the Clerk invitation, which has
            a lead time. Nothing else about these people needs setting up.
            {/*
              The distinct people, not the department count. One person sponsors several departments
              here, so the department figure overstates the work threefold — and the actual task is
              a list of addresses.
            */}
            <ul className="mt-2 grid gap-0.5">
              {uninvited(data.sponsor_without_account).map((person) => (
                <li key={person.email ?? person.full_name}>
                  {person.full_name}{' '}
                  <span className="text-muted-foreground">
                    {person.email ?? 'no email on record'}
                  </span>
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}

        <Card title="Sponsor coverage">
          {coverage.isPending ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : !data || data.departments.length === 0 ? (
            <EmptyState title="No bonding departments found">
              Run the employee sync — departments are derived from EMS (D-20).
            </EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Department</Th>
                  <Th>Sponsors</Th>
                  <Th>Accounts</Th>
                  <Th>Who</Th>
                </tr>
              </thead>
              <tbody>
                {data.departments.map((row) => (
                  <tr key={row.department_id}>
                    <Td>{row.department_name}</Td>
                    <Td>
                      {row.sponsor_count === 0 ? (
                        <Badge tone="bad">none</Badge>
                      ) : (
                        <Badge tone="good">{row.sponsor_count}</Badge>
                      )}
                    </Td>
                    <Td>
                      {row.sponsor_count === 0 ? (
                        <span className="text-muted-foreground text-xs">—</span>
                      ) : row.active_account_count === 0 ? (
                        <Badge tone="warn">needs a Clerk invitation</Badge>
                      ) : (
                        <Badge tone="good">{row.active_account_count}</Badge>
                      )}
                    </Td>
                    <Td className="text-xs">
                      {row.sponsors.length === 0 ? (
                        '—'
                      ) : (
                        <ul className="grid gap-0.5">
                          {row.sponsors.map((sponsor) => (
                            <li key={sponsor.employee_no}>
                              {sponsor.full_name}
                              <span className="text-muted-foreground">
                                {' '}
                                {sponsor.email ?? 'no email on record'}
                              </span>
                              {sponsor.has_account ? null : (
                                <span className="text-[var(--color-warning)]"> · no account</span>
                              )}
                              {sponsor.deactivated ? (
                                <span className="text-destructive"> · deactivated</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Assignments />
        <Settings />
      </div>
    </>
  );
}

/** The distinct people behind a set of uncovered departments — one person sponsors several. */
function uninvited(
  rows: readonly CoverageRow[],
): Array<{ full_name: string; email: string | null }> {
  const people = new Map<string, { full_name: string; email: string | null }>();
  for (const row of rows) {
    for (const sponsor of row.sponsors) {
      if (sponsor.has_account && !sponsor.deactivated) continue;
      people.set(sponsor.email ?? sponsor.full_name, {
        full_name: sponsor.full_name,
        email: sponsor.email,
      });
    }
  }
  return [...people.values()].sort((a, b) => a.full_name.localeCompare(b.full_name));
}

// -------------------------------------------------------------------- HR Directors --------

/**
 * Designating the HR Directors.
 *
 * A set of checkboxes rather than add/remove, because "here is who they are now" is one intention
 * — and because the readiness line underneath depends on the whole set, not on any one row.
 *
 * The two ways this can be wrong are both shown: nobody designated, and *everybody* designated.
 * The second is the one people do not expect — the verifier and the approver must differ, so a
 * company whose only admins are all Directors cannot complete a claim either.
 */
function Directors() {
  const session = useSession();
  const cache = useQueryClient();
  const owner = session.me.user.role === 'superadmin';
  const [chosen, setChosen] = useState<number[] | null>(null);

  const directors = useQuery({ queryKey: ['hr-directors'], queryFn: api.hrDirectors });

  const current = useMemo(
    () => (directors.data?.directors ?? []).map((row) => row.user_id),
    [directors.data],
  );
  const selection = chosen ?? current;

  const save = useMutation({
    mutationFn: () => api.setHrDirectors(selection),
    onSuccess: async () => {
      setChosen(null);
      await cache.invalidateQueries({ queryKey: ['hr-directors'] });
      await cache.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const readiness = directors.data?.readiness;
  const dirty =
    chosen !== null &&
    (chosen.length !== current.length || chosen.some((id) => !current.includes(id)));

  return (
    <Card
      title="HR Directors"
      action={
        owner && dirty ? (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setChosen(null)}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        ) : null
      }
    >
      {directors.isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <div className="grid gap-4">
          {readiness && !readiness.ok ? (
            <Alert
              tone="bad"
              title={
                readiness.directors === 0
                  ? 'No claim can be approved'
                  : 'No claim can be approved — every admin is a Director'
              }
            >
              {readiness.directors === 0 ? (
                <>
                  Final approval needs a designated HR Director. Until somebody is designated, every
                  claim will reach the last step and stop there.
                </>
              ) : (
                <>
                  The verifier and the approver must be different people (D-39), and all{' '}
                  {readiness.admins} admins are Directors — so whoever verifies a claim cannot
                  approve it and nobody else can either. Leave at least one admin undesignated.
                </>
              )}
            </Alert>
          ) : readiness ? (
            <Alert tone="good" title="Claims can complete">
              {readiness.directors} {readiness.directors === 1 ? 'Director' : 'Directors'} and{' '}
              {readiness.non_director_admins} other{' '}
              {readiness.non_director_admins === 1 ? 'admin' : 'admins'} — enough for the verifier
              and the approver to be different people (D-39).
            </Alert>
          ) : null}

          <div className="grid gap-2">
            {(directors.data?.eligible ?? []).map((person) => (
              <Checkbox
                key={person.user_id}
                checked={selection.includes(person.user_id)}
                disabled={!owner || save.isPending}
                onChange={(event) =>
                  setChosen(
                    event.target.checked
                      ? [...selection, person.user_id]
                      : selection.filter((id) => id !== person.user_id),
                  )
                }
                label={
                  <>
                    {person.email}{' '}
                    <span className="text-muted-foreground text-xs">
                      {ROLE_LABEL[person.role ?? 'staff'] ?? person.role}
                    </span>
                  </>
                }
                hint={
                  current.includes(person.user_id)
                    ? `Designated ${formatInstant(
                        directors.data?.directors.find((row) => row.user_id === person.user_id)
                          ?.since,
                      )}`
                    : undefined
                }
              />
            ))}
          </div>

          <p className="text-muted-foreground text-xs">
            Only <code>admin</code> and <code>superadmin</code> appear here: the designation adds
            final-approval rights on top of an HR role, it does not grant one. Several may be
            designated and any one may approve, so one person&rsquo;s leave does not stop every
            claim in the company (D-38).
            {owner ? null : ' Changing this needs superadmin.'}
          </p>

          {save.error ? <ErrorNote error={save.error} /> : null}
        </div>
      )}
    </Card>
  );
}

// --------------------------------------------------------------------- assignments --------

/**
 * Manager and Sponsor assignments, per department.
 *
 * `source` is on every row because it decides what the next sync will do: it refreshes what it
 * created and leaves manual rows alone (D-41). Without that badge, somebody "fixes" an EMS head
 * and the sync restores it overnight.
 */
function Assignments() {
  const cache = useQueryClient();
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [includeEnded, setIncludeEnded] = useState(false);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<'manager' | 'head_sponsor'>('manager');

  const departments = useQuery({ queryKey: ['departments'], queryFn: api.departments });

  const assignments = useQuery({
    queryKey: ['assignments', departmentId, includeEnded],
    queryFn: () => api.assignments(departmentId as number, includeEnded),
    enabled: departmentId !== null,
  });

  const candidates = useQuery({
    queryKey: ['employees', 'assign', departmentId, search],
    queryFn: () =>
      api.employees({ department_id: departmentId as number, search: search.trim(), limit: 10 }),
    enabled: departmentId !== null && search.trim().length >= 2,
  });

  const invalidate = async () => {
    await cache.invalidateQueries({ queryKey: ['assignments'] });
    await cache.invalidateQueries({ queryKey: ['sponsor-coverage'] });
  };

  const add = useMutation({
    mutationFn: (employeeId: number) =>
      api.updateAssignments(departmentId as number, {
        add: [{ employee_id: employeeId, assignment: kind }],
      }),
    onSuccess: async () => {
      setSearch('');
      await invalidate();
    },
  });

  const end = useMutation({
    mutationFn: (id: number) => api.updateAssignments(departmentId as number, { end: [id] }),
    onSuccess: invalidate,
  });

  return (
    <Card
      title="Managers and Sponsors"
      action={
        <Select
          aria-label="Department"
          className="h-8 w-64 py-1 text-xs"
          value={departmentId ?? ''}
          onChange={(event) => setDepartmentId(Number(event.target.value) || null)}
        >
          <option value="">Choose a department…</option>
          {(departments.data?.departments ?? []).map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </Select>
      }
    >
      {departmentId === null ? (
        <p className="text-muted-foreground text-sm">
          Pick a department to see who manages and who sponsors it.
        </p>
      ) : (
        <div className="grid gap-4">
          {assignments.isPending ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : (assignments.data?.assignments ?? []).length === 0 ? (
            <Alert tone="warn" title="Nobody is assigned">
              A department with no Sponsor cannot route a claim, and one with no Manager has nobody
              to raise a proposal.
            </Alert>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Who</Th>
                  <Th>Role</Th>
                  <Th>Source</Th>
                  <Th>Account</Th>
                  <Th>From</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {(assignments.data?.assignments ?? []).map((row) => (
                  <tr key={row.id} className={row.effective_to ? 'text-muted-foreground' : ''}>
                    <Td>
                      {row.full_name}
                      <p className="text-muted-foreground text-xs">
                        {row.employee_no}
                        {row.email ? ` · ${row.email}` : ''}
                      </p>
                    </Td>
                    <Td>
                      <Badge tone={row.assignment === 'head_sponsor' ? 'progress' : 'neutral'}>
                        {row.assignment === 'head_sponsor' ? 'Sponsor' : 'Manager'}
                      </Badge>
                    </Td>
                    <Td className="text-xs">
                      {row.source === 'ems' ? (
                        <Badge title="Refreshed by the nightly sync">
                          from EMS{row.ems_role ? ` (${row.ems_role})` : ''}
                        </Badge>
                      ) : (
                        <Badge title="The sync leaves this alone">added manually</Badge>
                      )}
                    </Td>
                    <Td>
                      {row.has_account ? (
                        <Badge tone="good">yes</Badge>
                      ) : (
                        <Badge tone="warn">not yet</Badge>
                      )}
                    </Td>
                    <Td className="text-xs whitespace-nowrap">
                      {formatDate(row.effective_from)}
                      {row.effective_to ? ` – ${formatDate(row.effective_to)}` : ''}
                    </Td>
                    <Td>
                      {row.effective_to ? null : (
                        <Button size="sm" busy={end.isPending} onClick={() => end.mutate(row.id)}>
                          End
                        </Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <div className="border-border grid gap-3 border-t pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-56 flex-1">
                <Field label="Add somebody" hint="Search this department. At least two characters.">
                  {(props) => (
                    <Input
                      {...props}
                      value={search}
                      placeholder="Name or employee number"
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  )}
                </Field>
              </div>
              <Select
                aria-label="As what"
                className="w-40"
                value={kind}
                onChange={(event) => setKind(event.target.value as 'manager' | 'head_sponsor')}
              >
                <option value="manager">as Manager</option>
                <option value="head_sponsor">as Sponsor</option>
              </Select>
            </div>

            {search.trim().length >= 2 && !candidates.isPending ? (
              <ul className="grid gap-1">
                {(candidates.data?.employees ?? []).map((employee) => (
                  <li key={employee.id} className="flex items-center justify-between gap-2 text-sm">
                    <span>
                      {employee.full_name}{' '}
                      <span className="text-muted-foreground text-xs">{employee.employee_no}</span>
                    </span>
                    <Button size="sm" busy={add.isPending} onClick={() => add.mutate(employee.id)}>
                      Add
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}

            <Checkbox
              checked={includeEnded}
              onChange={(event) => setIncludeEnded(event.target.checked)}
              label="Show assignments that have ended"
              hint="Ending an assignment keeps the row and stamps the date — it is not a delete."
            />

            {add.error ? <ErrorNote error={add.error} /> : null}
            {end.error ? <ErrorNote error={end.error} /> : null}
          </div>
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------------ settings --------

const SETTING_LABELS: Record<string, { label: string; hint: string; numeric?: boolean }> = {
  evidence_archive_url: {
    label: 'Evidence archive',
    hint: 'The org Shared Drive HR copies claim evidence into (D-22).',
  },
  prior_period_notice: {
    label: 'Prior-period notice',
    hint: 'Shown to managers until the 2026 history is loaded (D-23). Blank hides it.',
  },
  max_proposal_horizon_days: {
    label: 'Proposal horizon (days)',
    hint: 'How far ahead an event may be proposed (D-47). The calculator enforces this.',
    numeric: true,
  },
  advisory_lead_days: {
    label: 'Advisory lead time (days)',
    hint: 'Below this the manager is warned that HR may not have time — never blocked.',
    numeric: true,
  },
};

function Settings() {
  const session = useSession();
  const cache = useQueryClient();
  const owner = session.me.user.role === 'superadmin';
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings });

  const save = useMutation({
    mutationFn: (input: { key: string; value: string }) => {
      const numeric = SETTING_LABELS[input.key]?.numeric === true;
      return api.setSetting(input.key, numeric ? Number(input.value) : input.value);
    },
    onSuccess: async (_result, input) => {
      setDrafts((current) => {
        const next = { ...current };
        delete next[input.key];
        return next;
      });
      await cache.invalidateQueries({ queryKey: ['settings'] });
      // The horizon feeds the calculator, so every cached figure is now potentially wrong.
      await cache.invalidateQueries({ queryKey: ['calculate-roster'] });
      await cache.invalidateQueries({ queryKey: ['calculate-selection'] });
    },
  });

  const stored = new Map(
    (settings.data?.settings ?? []).map((row) => [row.key, String(row.value)]),
  );
  const keys = settings.data?.known_keys ?? [];

  return (
    <Card title="Settings">
      {settings.isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <div className="grid gap-4">
          {keys.map((key) => {
            const meta = SETTING_LABELS[key] ?? { label: key, hint: '' };
            const value = drafts[key] ?? stored.get(key) ?? '';
            const changed = drafts[key] !== undefined && drafts[key] !== (stored.get(key) ?? '');
            return (
              <div key={key} className="flex flex-wrap items-end gap-3">
                <div className="min-w-56 flex-1">
                  <Field label={meta.label} hint={meta.hint}>
                    {(props) => (
                      <Input
                        {...props}
                        value={value}
                        disabled={!owner}
                        inputMode={meta.numeric ? 'numeric' : 'text'}
                        onChange={(event) =>
                          setDrafts((current) => ({ ...current, [key]: event.target.value }))
                        }
                      />
                    )}
                  </Field>
                </div>
                {owner ? (
                  <Button
                    busy={save.isPending}
                    disabled={!changed || value.trim() === ''}
                    onClick={() => save.mutate({ key, value })}
                  >
                    Save
                  </Button>
                ) : null}
              </div>
            );
          })}

          <p className="text-muted-foreground text-xs">
            A short list on purpose. These are the only keys anything reads, and the value&rsquo;s
            shape is validated per key — an open settings endpoint would let anybody write anything
            under any name, including a shape whose reader expects something else.
          </p>

          {save.error ? <ErrorNote error={save.error} /> : null}
        </div>
      )}
    </Card>
  );
}
